// <copyright>Copyright (c) Microsoft Corporation. All rights reserved.</copyright>
/**
 * WARN: Usages of this function should be accompanied by a comment that explains why the usage is absolutely necessary.
 *
 * BEFORE USAGE:
 * - Consider fixing the types and/or type-narrowing if a type is nullable while value is never `null` or `undefined`.
 *  in a certain context.
 * - Consider handling "null | undefined" case gracefully with user feedback or meaningful default value/behavior
 * instead of throwing an error which is likely to display a meaningless error dialog to the user.
 *
 * Note that constraints and expectation change over time as the code does, therefore a value that can never be `undefined`
 *  or `null` at a certain time, may become `undefined or `null` expectedly in the future.
 */ function assertDefined(value, getErrorMessage) {
    ensureDefined(value, getErrorMessage);
}
/**
 * WARN: Usages of this function should be accompanied by a comment that explains why the usage is absolutely necessary.
 *
 * BEFORE USAGE:
 * - Consider fixing the types and/or type-narrowing if a type is nullable while value is never `null` or `undefined`.
 *  in a certain context.
 *
 * WARN: Never use this except in the following scenarios and there is no other way to work around;
 * Scenario 1
 * ----------
 * When we need to pass a T | null | undefined as the parameter of an external function or as the property of an
 * external interface for which these three conditions apply:
 *   1. the parameter or property is typed as T
 *   2. we cannot change the type of the parameter or property to be T | null | undefined, because it's external
 *   3. we know that the external API will accept null or undefined for that parameter or property without
 *      breaking, and we can ensure that it will stay that way in the future.
 *
 * In this case, you must notify the team that owns the external API so that they can fix their types and follow up
 *  later to remove the usage of `considerDefined` once the external API typing is fixed.
 *
 * Scenario 2
 * ----------
 * When we need to pass a null | undefined to a variable or to an interface property, and these three conditions apply:
 *   1. the variable or property is typed as T.
 *   2. we don't want to change the type of the variable or property to be T | null | undefined, because that would
 *      propagate the nullable type to too many places including public APIs.
 *   3. this is a harmless assignment despite the strict type violation because we don't expect the value to be
 *      actually accessed.
 */ function considerDefined(value) {
    return value;
}
/**
 * WARN: Usages of this function should be accompanied by a comment that explains why the usage is absolutely necessary.
 *
 * BEFORE USAGE:
 * - Consider fixing the types and/or type-narrowing if a type is nullable while value is never `null` or `undefined`.
 *  in a certain context.
 * - Consider handling "null | undefined" case gracefully with user feedback or meaningful default value/behavior
 * instead of throwing an error which is likely to display a meaningless error dialog to the user.
 *
 * Note that constraints and expectation change over time as the code does, therefore a value that can never be `undefined`
 *  or `null` at a certain time, may become `undefined or `null` expectedly in the future.
 */ function ensureDefined(value, getErrorMessage) {
    if (value == null) {
        throw new Error(getErrorMessage ? getErrorMessage() : "Value should be defined");
    }
    return value;
}

export { assertDefined, considerDefined, ensureDefined };
//# sourceMappingURL=StrictUtils.js.map
